import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { LanguageToolPage } from '@/components/astro/new-design-25/SEO/language-tool-page';
import { hreflangLinks, seo } from '@/seo/seo';

const faqTitle = 'Japanese text to speech FAQ';
const faqDescription =
  'Quick answers about converting Japanese text to speech with Listening.';
const faqs = [
  {
    question: 'Which Japanese voices are available?',
    answer:
      'Listening offers natural Japanese voices in male and female options, trained on real native Japanese speech across academic, news, and professional content.',
  },
  {
    question: 'How are kanji readings handled?',
    answer:
      'Common kanji and standard compound words are read correctly in context, including on-yomi and kun-yomi distinctions. Mixed kanji, hiragana, and katakana within a sentence flow naturally.',
  },
  {
    question: 'Is the pitch accent natural?',
    answer:
      'Yes. Our voices produce natural Japanese pitch accent rather than the flat low-intonation drone common in free Japanese TTS.',
  },
  {
    question: 'Can I use this for JLPT listening practice?',
    answer:
      'Yes. Paste any Japanese article, news story, or essay, slow it to 0.7x, and shadow native pronunciation. Useful from N3 all the way up to N1 preparation.',
  },
];

const heroTitle = 'Japanese Text to Speech';
const heroDescription =
  'Japanese text to speech with natural Japanese voices. Accurate handling of kanji, hiragana, and katakana, with proper academic terminology, on-yomi and kun-yomi readings, and natural Japanese pitch accent.';

const featureTitle = 'the Japanese TTS that gets the readings right:';
const featureSubtitle = '';
const featureDescription = '';
const featureBadge = 'Japanese voices';
const featureVariant = 'default';
const featureCtaText = 'Try now';

const featureFeatures = [
  {
    title: 'Natural Japanese Voices',
    description:
      'Voices trained on real Japanese speech with natural pitch accent and rhythm, not flat phoneme readouts.',
  },
  {
    title: 'Kanji Reading Selection',
    description:
      'Correctly handles common kanji and standard compound words, including on-yomi/kun-yomi distinctions in context.',
  },
  {
    title: 'Academic and Technical Japanese',
    description:
      'Trained on academic, scientific, and professional Japanese, not just conversational or anime-style speech.',
  },
  {
    title: 'JLPT Learner Friendly',
    description:
      'Slow speeds, synced highlighting in the app, and accurate pronunciation make this useful for N3–N1 listening practice.',
  },
];

const solutionBenefitsData = {
  title: 'Why Japanese readers and learners keep this TTS',
  items: [
    {
      text: 'Natural Japanese voices with proper pitch accent, not a flat low-intonation drone',
      highlight: 'proper pitch accent',
    },
    {
      text: 'Reliable kanji readings for common academic, technical, and everyday compounds',
      highlight: 'kanji readings',
    },
    {
      text: 'Correct handling of mixed kanji, hiragana, and katakana within a single sentence',
      highlight: 'mixed kanji, hiragana, and katakana',
    },
    {
      text: 'Adjustable speed from 0.5x to 4x, ideal for JLPT listening practice and shadowing drills',
      highlight: 'JLPT listening practice',
    },
  ],
};

const useCasesData = {
  title: 'How people use Japanese text to speech',
  description:
    'From students at Japanese universities to JLPT candidates and translators, here is how our Japanese voices are used.',
  useCases: [
    {
      title: 'Student at a Japanese University',
      challenge:
        'Work through Japanese academic reading: textbooks, research papers, course handouts in fields from law to engineering.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Convert academic Japanese text to audio with proper readings for technical vocabulary and compound terms.',
        },
        {
          label: 'Generic TTS',
          content:
            'Wrong kanji readings on academic words make the audio unreliable, defeating the purpose of listening.',
        },
      ],
    },
    {
      title: 'JLPT N3 to N1 Candidate',
      challenge:
        'Build real listening skill for the JLPT and for using Japanese outside textbook contexts.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Paste any Japanese article, news story, or essay, slow it down, and shadow with synced text in the app.',
        },
        {
          label: 'Generic TTS',
          content:
            'Robotic pacing and pitch trains the wrong ear, then real Japanese audio feels twice as fast as it is.',
        },
      ],
    },
    {
      title: 'Japanese-to-English Translator',
      challenge:
        'Review long Japanese source documents efficiently without rereading every kanji from scratch each time.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Listen while reading, with audio handling the readings so eyes can focus on meaning instead of decoding.',
        },
        {
          label: 'Generic TTS',
          content:
            'Mispronunciations introduce ambiguity that slows the work instead of speeding it up.',
        },
      ],
    },
    {
      title: 'Heritage Speaker Rebuilding Japanese',
      challenge:
        'Reconnect with Japanese as an adult who grew up speaking it but never fully learned the writing system.',
      solutionSections: [
        {
          label: 'Listening',
          content:
            'Hear native Japanese audio for any kanji-heavy text, with synced highlighting to map sound to character.',
        },
        {
          label: 'Generic TTS',
          content:
            'Off-natural pitch accent and wrong kanji readings actively interfere with rebuilding a native ear.',
        },
      ],
    },
  ],
};

const seoSections = [
  {
    title: 'What Japanese Text to Speech Has to Get Right',
    content:
      'Japanese is one of the hardest languages for TTS. Three scripts mix together within a single sentence, kanji have multiple readings that depend on context, pitch accent changes meaning, and the rhythm of Japanese is built on morae rather than syllables. Most free Japanese TTS gets the surface right (it makes sounds in roughly the right order) and the depth completely wrong (pitch accent flat, common compounds mispronounced, technical vocabulary fumbled).',
    contentSecondary:
      'Listening uses Japanese voices trained on real native speech across academic, news, and professional content. The voices read mixed-script Japanese the way a Japanese speaker actually reads it — with the right reading for the context, natural pitch accent, and the cadence that makes Japanese audio comfortable instead of jarring. For learners and serious readers, that is the difference between TTS that helps you build a Japanese ear and TTS that quietly trains the wrong one.',
  },
  {
    title: 'Japanese Text to Speech for Study, Translation, and Practice',
    content:
      'For Japanese learners, the bottleneck is rarely vocabulary lists. It is getting enough natural Japanese input at a speed you can follow. Real Japanese podcasts, news, and dramas come at full native speed. Our Japanese TTS lets you take any Japanese text and convert it into audio at exactly the speed you need, with synced highlighting in the Listening app so you can read along while you build your ear.',
    contentSecondary:
      'For Japanese-language professionals, the same tool turns long Japanese documents into ambient input. Translators, journalists, and researchers use the converter on this page to triage Japanese source material and to review final translations against the original. The result opens as a 24-hour read-along link with synced text, audio, speed controls, and a playbar. The same Japanese voices are available across the Listening web app, mobile apps, and Chrome extension.',
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/japanese-text-to-speech',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'Japanese Text to Speech — Natural Japanese AI Voices | Listening',
        description:
          'Japanese text to speech with natural Japanese voices. Handles kanji, hiragana, katakana, and academic terminology with proper pitch accent. Free demo.',
      }),
    ],
    links: [...hreflangLinks('/japanese-text-to-speech')],
  }),
  component: () => (
    <LanguageToolPage
      converterHeading={heroTitle}
      converterSubtitle={heroDescription}
      faqDescription={faqDescription}
      faqTitle={faqTitle}
      faqs={faqs}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      preferredLang='ja'
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
  ),
});
